/* oxlint-disable no-console */
import { createSelector } from 'reselect'
import { getExperienceProductIdFromProvider, getExperienceProviderFromOffer } from 'analytics/snowplow/helpers/itemCategorisation'
import type { DiscountRequestItem, DiscountRequestLoyaltyItem, DiscountRequestOrder } from 'api/mappers/promoMap'
import type { ExperienceOrderCreationItem } from 'api/order'
import { isExperienceUpsellSequence } from 'checkout/lib/experiences/experienceUpsell'
import { getPayableAccommodationSurcharge } from 'checkout/lib/utils/accommodation/priceView'
import { getCarHireSelectedRateOption } from 'checkout/lib/utils/carHire/view'
import { getTourV2ItemViewPrice } from 'checkout/lib/utils/tours/priceView'
import { getIsPostPurchaseTourChangeDates, getIsPostPurchaseTourOptionalExperience } from 'checkout/selectors/cartModeSelectors'
import getCheckoutPointsCalculationRequests from 'checkout/selectors/loyalty/pointsCalculation/getCheckoutPointsCalculationRequests'
import { getFormattedExperienceItems } from 'checkout/selectors/payment/orderCreation/getFormattedExperienceItems'
import { getFlightItemViewLuxPlusRegularPrice } from 'checkout/selectors/view/flights'
import { getIsEnabledFlightsBundledLuxPlusPricing, getIsEnabledFlightsLuxPlusPricing } from 'luxPlus/selectors/featureToggle'
import getAllItemViewsByType from 'checkout/selectors/view/getAllItemViewsByType'
import getAccommodationV2ItemViews from 'checkout/selectors/view/itemViews/getAccommodationV2ItemViews'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import config from 'constants/config'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_CRUISE } from 'constants/offer'
import { partitionBy, sum } from 'lib/array/arrayUtils'
import { objectKeys, flattenObjectKeys } from 'lib/object/objectUtils'
import { resolveChannelMarkupOrderIdentity } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupV2OfferPricing } from 'lib/channelMarkup/channelMarkupV2'
import { getChannelMarkupBandPricing, getOfferChannelMarkupLeadPricing } from 'lib/channelMarkup/channelMarkupLeadPricing'
import { isAndroidAppUserAgent, isIOSAppUserAgent } from 'lib/web/deviceUtils'
import { getWhiteLabelAppConfig } from 'lib/whitelabels/whitelabels'
import { isAccommodationV2AccommodationOfferView } from 'lib/checkout/checkoutUtils'
import { getAccommodationV2RateIdentity } from 'lib/checkout/accommodation/cart'
import { getChannelMarkupLeadOffer, getChannelMarkupSource } from 'selectors/channelMarkupSelector'
import { reportClientError } from 'services/errorReportingService'
import type { BaseSpecificPromoTypeLookup, PromoTypeLookup } from './filterItemDiscounts'

export type PromoItemDiscountsWithStatus = {
  hasRequiredPromoCodeData: boolean
  itemDiscounts: Array<App.Checkout.ItemDiscount>
}

export const PROMO_ORDER_ITEMS_CLIENT_ORDER_VERSION = 45

export function getTourV1PromoLookupType(item: App.CheckoutCartState['items']['0']): PromoTypeLookup {
  if (item.itemType == 'cruise') {
    return {
      specificType: 'cruise',
    }
  } else if (item.itemType == 'tourV1') {
    return {
      specificType: 'tour',
    }
  } else {
    console.warn('getTourV1PromoLookupType-fall through')
    return {
      specificType: 'tour',
    }
  }
}

type PromoCategory = {
  categoryBK: DiscountRequestItem['categoryBK']
  subCategoryBK?: DiscountRequestItem['subCategoryBK']
}

type PromoItemModifierInfo = NonNullable<DiscountRequestItem['itemModifierInfo']>

const getPromoItemCategoriesFromItemType = (itemType: string): PromoCategory => {
  switch (itemType) {
    case 'hotel':
      return { categoryBK: 'hotel', subCategoryBK: 'hotel-hotel' }
    case 'villa':
      return { categoryBK: 'hotel', subCategoryBK: 'hotel-rental' }
    case 'tourV1':
      return { categoryBK: 'tour' }
    case 'bedbankHotel':
      return { categoryBK: 'hotel', subCategoryBK: 'hotel-bedbank_hotel' }
    case 'tourV2experience':
    case 'tourV2Experience':
      return { categoryBK: 'tour', subCategoryBK: 'tour-tour_optional_experience_items' }
    case 'tourV2':
      // QQQ: add cruise "HolidayType" check here?
      return { categoryBK: 'tour' }
    case 'cruise':
      return { categoryBK: 'cruise' }
    case 'gift_card':
      return { categoryBK: 'gift_card' }
    default:
      reportClientError(new Error(`promoMap:getPromoItemCategoriesFromItemType - Failed to map item via ${itemType}`))
      return { categoryBK: 'hotel' }
  }
}

const getExperienceItemSubCategory = (offerIdWithPrefix: string): DiscountRequestItem['subCategoryBK'] => {
  const provider = getExperienceProviderFromOffer(offerIdWithPrefix)
  const productId = getExperienceProductIdFromProvider(provider)

  switch (productId) {
    case 'led_exp':{
      return 'experience-led_exp'
    }
    case 'mus_exp': {
      return 'experience-mus_exp'
    }
    case 'rez_exp': {
      return 'experience-rez_exp'
    }
    // TODO: remove type assertions when contract-svc-promo includes these subcategories
    case 'dby_exp': {
      return 'experience-dby_exp' as DiscountRequestItem['subCategoryBK']
    }
    case 'klo_exp': {
      return 'experience-klo_exp' as DiscountRequestItem['subCategoryBK']
    }
    case 'asp_exp': {
      return 'experience-asp_exp' as DiscountRequestItem['subCategoryBK']
    }
    case 'cln_exp': {
      return 'experience-cln_exp' as DiscountRequestItem['subCategoryBK']
    }
    case 'cmc_exp': {
      return 'experience-cmc_exp' as DiscountRequestItem['subCategoryBK']
    }
  }
}

/**
 * The prices a v2 price band reads for this item, matching what order creation sends svc-order.
 * A lead-priced item (Flash, LPC, bedbank) reads the lead that priced it: `rateLeadPricing` when its
 * cart item kept the shopped rate's (a property page never loads the LPC or bedbank offer into the
 * store), else its offer's lead for the stay's nights — the lead package for LE, the "from" sell for
 * bedbank. A calendar-priced item always has its offer: without it the item has no checkout view, so
 * no promo item. `discountableTotal` carries the markup and is never read for these. Any other item
 * keeps reading its own total.
 */
function getItemChannelMarkupPricing(
  state: App.State,
  item: DiscountRequestItem,
  offerType: App.OfferType,
  rateLeadPricing: App.ChannelMarkupLeadPricing | undefined,
): ChannelMarkupV2OfferPricing | undefined {
  const offer = item.offerId ? getChannelMarkupLeadOffer(state, item.offerId) : undefined
  return getChannelMarkupBandPricing(offerType, {
    leadPricing: rateLeadPricing ?? getOfferChannelMarkupLeadPricing(offer, item.numberOfNights),
    ownPricing: { total: item.discountableTotal, luxPlusPrice: item.luxPlusPrice || undefined },
  })
}

function getItemModifierInfo(
  source: ReturnType<typeof getChannelMarkupSource>,
  item: DiscountRequestItem,
  offerType: App.OfferType,
  checkoutWithMemberPrice: boolean,
  markupPricing: ChannelMarkupV2OfferPricing | undefined,
): PromoItemModifierInfo | undefined {
  if (!item.offerId) {
    return undefined
  }

  const chargedMemberPrice = checkoutWithMemberPrice && item.luxPlusPrice != null && item.luxPlusPrice > 0
  const identity = resolveChannelMarkupOrderIdentity(
    item.offerId,
    offerType,
    source,
    markupPricing,
    { member: chargedMemberPrice },
  )
  if (!identity) {
    return undefined
  }

  return {
    version: identity.version === 'v1' ? '1' : '2',
    channelMarkupVersion: identity.version,
    channelMarkupId: identity.channelMarkupId,
    channelMarkupCTAText: null,
    channelMarkupMarkupId: 'markupId' in identity ? identity.markupId : null,
    channelMarkupAmount: null,
  }
}

function getChannelMarkupOfferType(item: DiscountRequestItem): App.OfferType | undefined {
  switch (item.subCategoryBK) {
    case 'hotel-hotel':
      return OFFER_TYPE_HOTEL
    case 'hotel-bedbank_hotel':
      return OFFER_TYPE_BED_BANK
    case 'hotel-tactical_ao_hotel':
      return OFFER_TYPE_ALWAYS_ON
    default:
      return undefined
  }
}

export const getPromoDeviceType = (state: App.State): DiscountRequestOrder['deviceType'] => {
  const wlApp = getWhiteLabelAppConfig()
  const browserName = state.system.rawUserAgentString

  if (wlApp.isIOS || isIOSAppUserAgent(browserName)) {
    return 'ios'
  }

  if (wlApp.isAndroid || isAndroidAppUserAgent(browserName)) {
    return 'android'
  }

  return 'web'
}

type CountryCodeValidationSupportedType = App.Checkout.LEAccommodationOfferView | App.Checkout.TourV2AccommodationOfferView

export const getItemCountryCode = (item: CountryCodeValidationSupportedType): string | undefined => {
  // Some limited support for single country tour_v2 item destination validation
  if (item.offerType === 'tour_v2') {
    const itinerary = item.itemViews?.[0]?.variation?.itinerary
    if (itinerary && itinerary.length > 0 && 'locationsVisitedDetails' in itinerary[0]) {
      return itinerary[0].locationsVisitedDetails?.[0]?.countryCode
    }
    return undefined
  }

  if (item.offer?.property?.geoData?.countryCode) {
    return item.offer?.property?.geoData?.countryCode
  }

  return item.locationCountryCode ?? undefined
}

export const getCruiseItemCountryCode = (item: App.Checkout.AccommodationOfferView): string | undefined => {
  if (item.itemViews.length > 0 && 'variation' in item.itemViews[0] && item.itemViews[0].variation?.itinerary) {
    const itinerary = item.itemViews[0].variation?.itinerary
    if (itinerary && itinerary.length > 0 && 'locationsVisitedDetails' in itinerary[0]) {
      return itinerary[0].locationsVisitedDetails?.[0]?.countryCode
    }
  }
  return undefined
}

export const getNumberOfOccupants = (item: App.Checkout.AccommodationItemView | App.Checkout.TourV2AccommodationItemView | App.Checkout.BedbankAccommodationItemView | App.Checkout.VillaItemView): {
  numberOfAdults: number | undefined
  numberOfChildren: number | undefined
} => {
  const itemToCheck = item.kind == 'villa' ? item.item : item

  return {
    numberOfAdults: itemToCheck.occupancy?.adults,
    numberOfChildren: (itemToCheck.occupancy?.teenagers ?? 0) + (itemToCheck.occupancy?.children ?? 0) + (itemToCheck.occupancy?.infants ?? 0),
  }
}

/**
 *
 * @param prefix a prefix added to the info string
 * @param item the item to build the info string from
 * @returns a string of item identifiers and values
 * Please don't use this directly for business logic, (it only supported on client request and designed to inform changes to the categoryBK/subCategoryBK mapping)
*/

type StandardType = App.Checkout.AccommodationOfferView
  | App.Checkout.TourV2AccommodationOfferView
  | App.Checkout.BedbankAccommodationOfferView
  | App.Checkout.VillaItemView
  | App.Checkout.CruiseAccommodationOfferView | App.Checkout.FlightItemView | App.Checkout.TransferItemView | App.Checkout.InsuranceItemView | App.Checkout.SubscriptionJoinItemView | App.Checkout.LuxPlusSubscriptionItemView | App.Checkout.CarHireItemView | App.Checkout.TourV2ExperienceItemView | App.Checkout.MerchandiseItemView | App.Checkout.MerchandiseShippingItemView
  | ReturnType<typeof getFormattedExperienceItems>['data'][number]
  | App.Checkout.MerchandiseItem
  | App.Checkout.MerchandiseShippingItem
  | App.Checkout.GiftCardItemView

type ConslidatedItemType = Array<App.Checkout.TransferItemView> | Array<App.Checkout.InsuranceItemView>

type BuildItemInfoStringProps = {
  prefix: string
  item: StandardType | ConslidatedItemType
  totals?: App.Checkout.ItemViewTotals
  fullDetails?: boolean
}

const includedIds = new Set([
  'id',
  'item.itemId',
  'uniqueKey',
  'provider_offer_id',
  'price',
  'title',
  'total',
  'memberTotal',
  'memberPrice',
  'surcharge',
  'taxesAndFees',
  'offerType',
  'designation',
  'itemType',
  'parentType',
  'reservationType',
  'price',
  'memberTotal',
  'total',
  'provider',
  'kind',
  'itemId',
  'offerId',
])

const fieldsToExclude = [
  'item.offer.facilities',
  'item.itemViews',
  'item.item.offer.reviewsBreakdown',
  'item.offer.roomTypePricing',
  'item.offer.purchasableExperienceOptions',
  'item.offer.departures',
  'item.offer.packages',
  'item.item.offer.copy',
  'item.offer.lowestPricePackage',
  'item.offer.badge',
  'item.offer.stats',
  'item.offer.images',
  'item.item.offer.dropOff.operatingHours',
  'item.offer.property.reviews',
  'item.cancellation_policies',
  'item.offer.facilityGroups',
  'item.offer.purchasableOptions',
  'item.offer.finePrint',
  'item.offer.variants',
  'item.offer.tileInclusions',
]

const marketingAddressItemInfoMaxJsonLength = 1200

function readLatLngPair(obj: unknown): { lat: number; lng: number } | undefined {
  if (!obj || typeof obj !== 'object') return undefined
  const o = obj as Record<string, unknown>
  const { latitude, longitude } = o
  if (typeof latitude === 'number' && typeof longitude === 'number') {
    return { lat: latitude, lng: longitude }
  }
  return undefined
}

/**
 * Walk offer/item nesting (same shape as marketing address) to find the first place with numeric lat/lng
 * (location / property, car-hire offer.pickUp / offer.dropOff, item-view pickUpLocation / returnLocation) for
 * `itemLat` / `itemLng` on the discount request.
 */
function findLatLngOnDiscountItem(node: unknown, depth = 0): { lat: number; lng: number } | undefined {
  if (depth > 6 || !node || typeof node !== 'object') return undefined
  const record = node as Record<string, unknown>
  const fromLocation = readLatLngPair(record.location)
  if (fromLocation) return fromLocation
  const fromProperty = readLatLngPair(record.property)
  if (fromProperty) return fromProperty
  const fromPickUp = readLatLngPair(record.pickUp)
  if (fromPickUp) return fromPickUp
  const fromDropOff = readLatLngPair(record.dropOff)
  if (fromDropOff) return fromDropOff
  const fromPickUpLocation = readLatLngPair(record.pickUpLocation)
  if (fromPickUpLocation) return fromPickUpLocation
  const fromReturnLocation = readLatLngPair(record.returnLocation)
  if (fromReturnLocation) return fromReturnLocation
  if (record.offer) {
    const nested = findLatLngOnDiscountItem(record.offer, depth + 1)
    if (nested) return nested
  }
  if (record.item) {
    return findLatLngOnDiscountItem(record.item, depth + 1)
  }
  return undefined
}

function discountRequestItemLatLngFields(
  item: StandardType | ConslidatedItemType,
): Partial<Pick<DiscountRequestItem, 'itemLat' | 'itemLng'>> {
  if (Array.isArray(item)) return {}
  const coords = findLatLngOnDiscountItem(item)
  if (!coords) return {}
  return { itemLat: coords.lat, itemLng: coords.lng }
}

function findMarketingAddressOnDiscountItem(item: unknown): Array<unknown> | undefined {
  if (!item || typeof item !== 'object') return undefined
  const record = item as Record<string, unknown>
  const location = record.location
  if (location && typeof location === 'object') {
    const marketingAddress = (location as Record<string, unknown>).marketingAddress
    if (Array.isArray(marketingAddress) && marketingAddress.length > 0) {
      return marketingAddress
    }
  }
  if (record.offer) {
    const nested = findMarketingAddressOnDiscountItem(record.offer)
    if (nested) return nested
  }
  if (record.item) {
    const nested = findMarketingAddressOnDiscountItem(record.item)
    if (nested) return nested
  }
  return undefined
}

function marketingAddressSnippetForItemInfo(item: StandardType | ConslidatedItemType): string | undefined {
  if (Array.isArray(item)) return undefined
  const marketingAddress = findMarketingAddressOnDiscountItem(item)
  if (!marketingAddress?.length) return undefined
  try {
    const json = JSON.stringify(marketingAddress)
    const body = json.length > marketingAddressItemInfoMaxJsonLength
      ? `${json.slice(0, marketingAddressItemInfoMaxJsonLength)}...`
      : json
    return `location.marketingAddress:${body}`
  } catch {
    return undefined
  }
}

export function buildItemInfoString({ prefix, item, totals, fullDetails = true }: BuildItemInfoStringProps): string {
  if (Array.isArray(item)) {
    return item.map((i) => buildItemInfoString({ prefix, item: i, fullDetails })).join(' ')
  }

  let totalsString = ''
  if (totals) {
    const flattenedTotals = flattenObjectKeys({ totals })
    totalsString = Object.entries(flattenedTotals)
      .map(([key, value]) => `${key}:${value}`)
      .join(' ')
  }

  // To long risks 503 Request too large errors
  const maxInfoStringLength = 3000

  const flattenedExtraItemDetails = flattenObjectKeys({ item }, '.', fieldsToExclude, 20)
  let extraItemDetails = Object.entries(flattenedExtraItemDetails)
    .map(([key, value]) => `${key}:${value}`)
    .join(' ')

  if (!extraItemDetails.includes('marketingAddress')) {
    const marketingSnippet = marketingAddressSnippetForItemInfo(item)
    if (marketingSnippet) {
      extraItemDetails = extraItemDetails ? `${extraItemDetails} ${marketingSnippet}` : marketingSnippet
    }
  }

  extraItemDetails = extraItemDetails.substring(0, maxInfoStringLength)

  const includedKeys = objectKeys(item).filter((key) => includedIds.has(key))
  const log = includedKeys.map((key) => `${key}:${item[key]}`).join(' ')
  return `${prefix} ${log} ${totalsString} ${extraItemDetails}`
}

export function getMx({ item, itemView }: SharedPromoItemFieldsProps): string | undefined {
  if (itemView && 'variant' in itemView && typeof itemView.variant === 'object' && 'mx' in itemView.variant && typeof itemView.variant.mx === 'string') {
    return itemView.variant.mx
  }

  if (item && 'mx' in item && typeof item.mx === 'string') {
    return item.mx
  }
  if (itemView && 'mx' in itemView && typeof itemView.mx === 'string') {
    return itemView.mx
  }
  return undefined
}

export function getLoyaltyOfferType({ item, itemView }: SharedPromoItemFieldsProps): string | undefined {
  if (item && 'loyalty' in item && typeof item.loyalty === 'object') {
    return item.loyalty?.productType
  }
  if (itemView && 'loyalty' in itemView && typeof itemView.loyalty === 'object') {
    return itemView.loyalty?.productType
  }
  return undefined
}

/**
 * Some item types rely entierly on the itemView (e.g. villa items)
 * We allow for either the item view of the item itself to be passed in
 * (we prefer the values from the item itself, but if the item view is passed in, we use the values from the item view)
 */
type SharedPromoItemFieldsProps = {
  item?: StandardType
  itemView?: App.Checkout.AnyItemView
  /** Friends & family flight pricing; false for every non-flight discount line. */
  isFnFBooking?: boolean
}

export function sharedPromoItemFields({ item, itemView, isFnFBooking = false }: SharedPromoItemFieldsProps): Partial<DiscountRequestItem> {
  return {
    isFnFBooking,
    mx: getMx({ item, itemView }),
    loyaltyOfferType: getLoyaltyOfferType({ item, itemView }),
  }
}

export const getDefaultTravellers = (state: App.State): DiscountRequestItem['travellers'] => state.checkout.form.travellerForms.map((traveller) => ({
  firstName: traveller.data.firstName || 'Unknown',
  lastName: traveller.data.lastName || 'Unknown',
}))

/**
 * An Array of DiscountIIDs is used to ensure we can correctly identify items and their associated discounts, in three ways:
 * - Which cart items the discounts are associated with
 * - Where/What total is rendered in the breakdown view (i.e. the "Promotion 'testcode'  -A$472" line in the summary display)
 * - Which order items the discounts are associated with on the order AFTER it has been placed (i.e. mapping the relevant discounts for each item is passed when we submit the order)
 */
export type DiscountIIDs = {
  offerId?: string
  itemId?: string
} | {
  offerId: string
} | {
  itemId: string
}

function sumViewItemsTotal(items: Array<App.Checkout.AnyItemView>): number {
  return sum(items, (itemView) => sumViewItemTotal(itemView))
}

function sumViewItemTotal(item: App.Checkout.AnyItemView): number {
  return item.totals.price + getPayableAccommodationSurcharge(item.totals)
}

function sumViewItemsMemberTotal(items: Array<App.Checkout.AnyItemView>): number {
  return sum(items, (itemView) => sumViewItemMemberTotal(itemView))
}

function sumViewItemMemberTotal(item: App.Checkout.AnyItemView): number {
  return item.totals.memberPrice > 0
    ? item.totals.memberPrice + getPayableAccommodationSurcharge(item.totals)
    : 0
}

function getNumberOfNights(itemView: App.Checkout.LEAccommodationItemView<App.LoyaltyAccommodationProductType> | App.Checkout.BedbankAccommodationItemView | App.Checkout.TourV2AccommodationItemView | App.Checkout.VillaItemView | App.Checkout.AccommodationV2AccommodationItemView): number | undefined {
  if (itemView.kind == 'villa' || itemView.kind == 'bedbank' || itemView.kind == 'tourV2' || itemView.kind == 'accommodationV2') {
    return itemView.item.duration
  }

  if (itemView.pkg?.duration) {
    return itemView.pkg.duration
  }

  if ('duration' in itemView) {
    return itemView.duration as number
  }
  return undefined
}

type DiscountRequestItemDateFields = Partial<Pick<DiscountRequestItem, 'startDate' | 'endDate'>>
type DiscountRequestFlightInfo = NonNullable<DiscountRequestItem['flightInfo']>
type DiscountRequestFlightInfoItem = DiscountRequestFlightInfo[number]
type DiscountRequestHotelInfo = NonNullable<DiscountRequestItem['hotelInfo']>

function discountRequestItemDateFields(start?: string | null, end?: string | null): DiscountRequestItemDateFields {
  const fields: DiscountRequestItemDateFields = {}
  const trimmedStart = start?.trim()
  const trimmedEnd = end?.trim()
  if (trimmedStart) {
    fields.startDate = trimmedStart
  }
  if (trimmedEnd) {
    fields.endDate = trimmedEnd
  }
  return fields
}

function discountRequestDatesFromAccommodationCartItem(
  item: App.Checkout.AccommodationItemView['item'],
): DiscountRequestItemDateFields {
  if ('checkIn' in item && item.checkIn && 'checkOut' in item && item.checkOut) {
    return discountRequestItemDateFields(item.checkIn, item.checkOut)
  }
  if ('startDate' in item || 'endDate' in item) {
    const withRange = item as { startDate?: string, endDate?: string }
    return discountRequestItemDateFields(withRange.startDate, withRange.endDate)
  }
  return {}
}

function discountRequestDatesFromFlightItem(item: App.Checkout.FlightItem): DiscountRequestItemDateFields {
  // Only use travelStart/travelEnd when both exist. A single populated field would
  // short-circuit with partial data even though `flights` often has full bounds.
  if (item.travelStart && item.travelEnd) {
    return discountRequestItemDateFields(item.travelStart, item.travelEnd)
  }
  const flights = item.flights
  if (!flights?.length) {
    return {}
  }
  const firstLeg = flights[0]
  const lastLeg = flights[flights.length - 1]
  return discountRequestItemDateFields(
    firstLeg.departureDateTime ?? firstLeg.departingDate,
    lastLeg.arrivalDateTime ?? lastLeg.departingDate,
  )
}

function discountRequestFlightInfo(view: App.Checkout.FlightItemView): DiscountRequestFlightInfo {
  return view.item.flights.map((cartFlight): DiscountRequestFlightInfoItem => {
    const journey = view.flights.find(flight => flight.journeyFlight.journeyKey === cartFlight.journeyKey)
    const flight = journey?.journeyFlight.flights[0]
    return {
      fareType: view.fareType,
      originAirportCode: cartFlight.departingAirportCode || view.item.originAirportCode,
      destinationAirportCode: cartFlight.arrivalAirportCode || view.item.destinationAirportCode,
      carrierCode: flight?.carrier ?? cartFlight.carrier ?? '',
      flightNumber: flight?.flightNumber ?? '',
    }
  })
}

function discountRequestHotelInfo(offerId?: string, rateId?: string): DiscountRequestHotelInfo | undefined {
  if (!offerId) {
    return undefined
  }

  return {
    version: '1',
    offerId,
    rateId,
  }
}

type DiscountRequestRoomRateItem = App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem | App.Checkout.LETourV1Item

function getAccommodationRoomRateId(item: DiscountRequestRoomRateItem): string | undefined {
  return 'roomRateId' in item ? item.roomRateId : undefined
}

function getAccommodationV2PromoSubCategory(
  offerType: App.Checkout.AccommodationV2AccommodationOfferView['offerType'],
): DiscountRequestItem['subCategoryBK'] {
  if (offerType === OFFER_TYPE_BED_BANK) return 'hotel-bedbank_hotel'
  if (offerType === OFFER_TYPE_HOTEL) return 'hotel-hotel'
  return 'hotel-tactical_ao_hotel'
}

function getAccommodationV2PromoProvider(
  item: App.Checkout.AccommodationV2AccommodationOfferView,
): string | undefined {
  return item.property?.name || item.mainLabel || item.location
}

/**
 * What the discount request prices an accommodationV2 room on.
 *
 * The rate identity is the one svc-order will book, which svc-promo re-prices the order against when
 * it logs the promo. A `fallback_selected` enquiry moves the room onto the other supplier's rate
 * without touching the cart item (hence `getAccommodationV2ItemService` reading the session), so the
 * live enquiry rate wins over the shopped identity on the item.
 */
function getAccommodationV2PromoPricing(
  view: App.Checkout.AccommodationV2AccommodationOfferView,
  itemView: App.Checkout.AccommodationV2AccommodationItemView,
) {
  const { offerId, internalRateId } = itemView.rate ? getAccommodationV2RateIdentity(itemView.rate) : itemView.item
  return {
    subCategoryBK: getAccommodationV2PromoSubCategory(view.offerType),
    offerId,
    rateId: internalRateId,
    discountableTotal: sumViewItemTotal(itemView),
    memberTotal: sumViewItemMemberTotal(itemView),
  }
}

/**
 * Fingerprint of what the discount request prices accommodationV2 rooms on. Checkout enquiry can move
 * these after a promo code or voucher was priced (a price change, or `fallback_selected` onto the other
 * supplier), which leaves the applied discount stale.
 */
export const getAccommodationV2PromoInputsKey = createSelector(
  (state: App.State) => getAccommodationV2ItemViews(state),
  (views): string => JSON.stringify(views.data.flatMap(view => view.itemViews.map(itemView => ({
    itemId: itemView.item.itemId,
    ...getAccommodationV2PromoPricing(view, itemView),
  })))),
)

/**
 * Whether the applied promo code / voucher discount was priced on accommodationV2 inputs that have since moved.
 */
export function hasAccommodationV2PromoInputsChanged(previousState: App.State, currentState: App.State): boolean {
  if (!currentState.checkout.promo) return false
  return getAccommodationV2PromoInputsKey(previousState) !== getAccommodationV2PromoInputsKey(currentState)
}

function discountRequestDatesFromExperienceOrderItem(exp: ExperienceOrderCreationItem): DiscountRequestItemDateFields {
  const ticket = exp.ticket
  const start = ticket.date ?? ticket.rateStartDate
  const end = ticket.rateEndDate ?? ticket.date
  return discountRequestItemDateFields(start, end)
}

function discountRequestDatesFromTourV2ExperienceItem(item: App.Checkout.TourV2ExperienceItem): DiscountRequestItemDateFields {
  if (!item.date) {
    return {}
  }
  return discountRequestItemDateFields(item.date, item.date)
}

function discountRequestDatesFromTransferItem(item: App.Checkout.TransferItem): DiscountRequestItemDateFields {
  const transfer = item.transfer
  const end = transfer.rateEndDate ?? transfer.date
  return discountRequestItemDateFields(transfer.date, end)
}

/**
 * (A simplified replacement for getPromoItems - generates 'Discount Request Item' (as opposed to 'Discount Request Item V2'))
 *
 * Please bump 'clientOrderVersion' when making changes to the logic or structure of the Discount Request Order/Items
 *
 * If adjusting the itemId or offerId below,  * ensure the relevant call to filterItemDiscounts is updated to match or the promo total will not be displayed in the breakdown view.
 * NB: (offer/itemId changes are encouraged if making it more relevant/useful for the vertical!)
 */
export function getPromoItems(state: App.State, clientOrderVersion: number): Array<DiscountRequestItem> {
  const resultItems: Array<DiscountRequestItem> = []
  // Lead pricing an accommodationV2 item's shopped rate carried, by `itemId`; see `getItemChannelMarkupPricing`.
  const rateLeadPricingByItemId = new Map<string, App.ChannelMarkupLeadPricing>()
  const channelMarkupSource = getChannelMarkupSource(state)
  const useMemberPrice = checkoutWithMemberPrice(state)
  try {
    const {
      accommodationItemsView,
      tourV2ExperienceItemsView,
      carHireItemsView,
      villaItemsView,
      transferItemsView,
      insuranceItemsView,
      subscriptionItemView,
      luxPlusSubscriptionItemView,
      flightItemsView,
      merchandiseItemsView,
      merchandiseShippingItemsView,
      giftCardItemsView,
    } = getAllItemViewsByType(state).data
    const isMemberOfHasSubItem = getHasLuxPlusOrSubscriptionInCart(state)

    const [cruiseItems, accommodationItems] = partitionBy(accommodationItemsView.data || [], (item) => !!(item.offerType == OFFER_TYPE_TOUR && 'offer' in item && item.offer && 'holidayTypes' in item.offer && item.offer.holidayTypes?.includes('Cruises')))

    for (const _ of cruiseItems || []) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'cruise'
      resultItems.push(...cruiseItems.map((item) => ({
        categoryBK,
        discountableTotal: sumViewItemsTotal(item.itemViews),
        luxPlusPrice: isMemberOfHasSubItem ? sumViewItemsMemberTotal(item.itemViews) : 0,
        numberOfNights: item.duration,
        numberOfAdults: item.occupancy.map((occupancy) => occupancy.adults).reduce((a, b) => a + b, 0),
        numberOfChildren: item.occupancy.map((occupancy) => (occupancy.children ?? 0) + (occupancy.infants ?? 0)).reduce((a, b) => a + b, 0),
        reservationType: item.reservationType,
        itemCountryCode: getCruiseItemCountryCode(item),
        itemInfoString: buildItemInfoString({ prefix: 'cruise-items-v1.1', item }),
        provider: 'offer' in item ? item.offer?.slug : undefined,
        offerId: item.offerId,
        travellers: [] as DiscountRequestItem['travellers'],
        ...discountRequestItemDateFields(item.startDate, item.endDate),
        ...discountRequestItemLatLngFields(item),
        ...sharedPromoItemFields({ item }),
      })))
    }

    for (const item of accommodationItems || []) {
      if (isAccommodationV2AccommodationOfferView(item)) {
        const categoryBK: DiscountRequestItem['categoryBK'] = 'hotel'
        resultItems.push(...item.itemViews.map((iv) => {
          const { subCategoryBK, offerId, rateId, discountableTotal, memberTotal } = getAccommodationV2PromoPricing(item, iv)
          if (iv.item.channelMarkupLeadPricing) {
            rateLeadPricingByItemId.set(iv.item.itemId, iv.item.channelMarkupLeadPricing)
          }
          return {
            categoryBK,
            subCategoryBK,
            discountableTotal,
            luxPlusPrice: isMemberOfHasSubItem ? memberTotal : 0,
            itemCountryCode: item.locationCountryCode,
            provider: getAccommodationV2PromoProvider(item),
            numberOfNights: item.duration,
            reservationType: item.reservationType,
            offerId,
            hotelInfo: discountRequestHotelInfo(offerId, rateId),
            itemId: iv.item.itemId,
            transactionKey: iv.item.transactionKey,
            itemInfoString: buildItemInfoString({ prefix: 'accommodation-v2-v1.1', item }),
            travellers: getDefaultTravellers(state),
            ...discountRequestDatesFromAccommodationCartItem(iv.item),
            ...discountRequestItemLatLngFields(item),
            ...sharedPromoItemFields({ item, itemView: iv }),
            ...getNumberOfOccupants(iv),
          }
        }))
        continue
      }

      if (item.offerType == OFFER_TYPE_BED_BANK) {
        const categoryBK: DiscountRequestItem['categoryBK'] = 'hotel'
        const subCategoryBK: DiscountRequestItem['subCategoryBK'] = 'hotel-bedbank_hotel'
        resultItems.push(...item.itemViews.map((iv) => {
          const roomRateId = getAccommodationRoomRateId(iv.item)
          return {
            categoryBK,
            subCategoryBK,
            discountableTotal: sumViewItemTotal(iv),
            luxPlusPrice: isMemberOfHasSubItem ? sumViewItemMemberTotal(iv) : 0,
            itemCountryCode: item.locationCountryCode,
            provider: item.offer?.property.name,
            numberOfNights: getNumberOfNights(iv),
            reservationType: item.reservationType,
            offerId: item.offerId,
            hotelInfo: discountRequestHotelInfo(item.offerId, roomRateId),
            itemId: iv.item.itemId,
            transactionKey: iv.item.transactionKey,
            itemInfoString: buildItemInfoString({ prefix: 'bedbank-items-v1.2', item }),
            travellers: getDefaultTravellers(state),
            ...discountRequestDatesFromAccommodationCartItem(iv.item),
            ...discountRequestItemLatLngFields(item),
            ...sharedPromoItemFields({ item: item, itemView: iv }),
            ...getNumberOfOccupants(iv),
          }
        }))
      } else if (item.offerType == OFFER_TYPE_HOTEL) {
        const categoryBK: DiscountRequestItem['categoryBK'] = 'hotel'
        const subCategoryBK: DiscountRequestItem['subCategoryBK'] = 'hotel-hotel'
        resultItems.push(...item.itemViews.map((iv) => {
          const roomRateId = getAccommodationRoomRateId(iv.item)
          return {
            categoryBK,
            subCategoryBK,
            itemId: iv.item.itemId,
            transactionKey: iv.item.transactionKey,
            discountableTotal: sumViewItemTotal(iv),
            luxPlusPrice: isMemberOfHasSubItem ? sumViewItemMemberTotal(iv) : 0,
            numberOfNights: getNumberOfNights(iv),
            provider: item.offer?.location,
            itemCountryCode: getItemCountryCode(item),
            reservationType: item.reservationType,
            offerId: item.offerId,
            hotelInfo: discountRequestHotelInfo(item.offerId, roomRateId),
            itemInfoString: buildItemInfoString({ prefix: 'hotel-v1.2', item, totals: iv.totals }),
            travellers: getDefaultTravellers(state),
            ...discountRequestDatesFromAccommodationCartItem(iv.item),
            ...discountRequestItemLatLngFields(item),
            ...sharedPromoItemFields({ item: item, itemView: iv }),
            ...getNumberOfOccupants(iv),
          }
        }))
      } else if (item.offerType == OFFER_TYPE_ALWAYS_ON) {
        const categoryBK: DiscountRequestItem['categoryBK'] = 'hotel'
        const subCategoryBK: DiscountRequestItem['subCategoryBK'] = 'hotel-tactical_ao_hotel'
        resultItems.push(...item.itemViews.map((iv) => {
          const roomRateId = getAccommodationRoomRateId(iv.item)
          return {
            categoryBK,
            subCategoryBK,
            itemId: iv.item.itemId,
            transactionKey: iv.item.transactionKey,
            discountableTotal: sumViewItemTotal(iv),
            luxPlusPrice: isMemberOfHasSubItem ? sumViewItemMemberTotal(iv) : 0,
            provider: item.offer?.locationHeading,
            numberOfNights: getNumberOfNights(iv),
            itemCountryCode: getItemCountryCode(item),
            reservationType: item.reservationType,
            offerId: item.offerId,
            hotelInfo: discountRequestHotelInfo(item.offerId, roomRateId),
            itemInfoString: buildItemInfoString({ prefix: 'always-on-v1.2', item }),
            travellers: getDefaultTravellers(state),
            ...discountRequestDatesFromAccommodationCartItem(iv.item),
            ...discountRequestItemLatLngFields(item),
            ...sharedPromoItemFields({ item: item, itemView: iv }),
            ...getNumberOfOccupants(iv),
          }
        }))
      } else if (item.offerType == OFFER_TYPE_TOUR_V2) {
        const categoryBK: DiscountRequestItem['categoryBK'] = 'tour'
        item.itemViews.forEach((iv) => {
          const isBundled = !!iv.item.ltleBundleId && !getIsPostPurchaseTourChangeDates(state) && !getIsPostPurchaseTourOptionalExperience(state)
          const { totalPrice, totalMemberPrice } = getTourV2ItemViewPrice(item.offer, iv.item.purchasableOption, iv.occupancy, isBundled)
          const totalSubtractedPaidPrice = totalPrice - (iv.totals.paidPrice ?? 0)

          let itemId = 'roomId' in iv.item.occupancy ? (iv.item.occupancy as { roomId: string }).roomId : undefined
          if (itemId == undefined) {
            itemId = iv.item.itemId
          }

          resultItems.push({
            categoryBK,
            discountableTotal: totalSubtractedPaidPrice > 0 ? totalSubtractedPaidPrice : totalPrice,
            luxPlusPrice: isMemberOfHasSubItem ? totalMemberPrice : 0,
            itemCountryCode: getItemCountryCode(item),
            numberOfNights: getNumberOfNights(iv),
            reservationType: item.reservationType,
            provider: item.operatorName ?? item.subLabel ?? item.mainLabel,
            itemInfoString: buildItemInfoString({ prefix: 'offerType-tourv2-v1.1', item, totals: iv.totals }),
            offerId: iv.item.purchasableOption.fkTourId,
            itemId: itemId,
            transactionKey: iv.item.transactionKey,
            ...discountRequestDatesFromAccommodationCartItem(iv.item),
            ...discountRequestItemLatLngFields(item),
            ...sharedPromoItemFields({ item: item, itemView: iv }),
            ...getNumberOfOccupants(iv),
          })
        })
      } else if (item.offerType == OFFER_TYPE_CRUISE) {
        const categoryBK: DiscountRequestItem['categoryBK'] = 'cruise'
        resultItems.push(...item.itemViews.map((iv) => ({
          categoryBK,
          discountableTotal: sumViewItemTotal(iv),
          luxPlusPrice: isMemberOfHasSubItem ? sumViewItemMemberTotal(iv) : 0,
          numberOfNights: item.duration,
          reservationType: item.reservationType,
          itemInfoString: buildItemInfoString({ prefix: 'itemType-cruise-v1.1', item }),
          offerId: item.offerId,
          transactionKey: iv.item.transactionKey,
          ...discountRequestDatesFromAccommodationCartItem(iv.item),
          ...discountRequestItemLatLngFields(item),
          ...sharedPromoItemFields({ item: item, itemView: iv }),
          ...getNumberOfOccupants(iv),
        })))
      } else if ('itemType' in item && typeof item.itemType == 'string') {
        const category = getPromoItemCategoriesFromItemType(item.itemType)
        switch (item.itemType) {
          case 'hotel': {
            resultItems.push(...item.itemViews.map((iv: App.Checkout.AccommodationItemView) => ({
              ...category,
              discountableTotal: sumViewItemTotal(iv),
              luxPlusPrice: isMemberOfHasSubItem ? sumViewItemMemberTotal(iv) : 0,
              numberOfNights: item.duration,
              itemCountryCode: getItemCountryCode(item),
              reservationType: item.reservationType,
              itemInfoString: buildItemInfoString({ prefix: 'itemType-hotel-v1.1', item }),
              offerId: item.offerId,
              itemId: iv.item.itemId,
              transactionKey: iv.item.transactionKey,
              ...discountRequestDatesFromAccommodationCartItem(iv.item),
              ...discountRequestItemLatLngFields(item),
              ...sharedPromoItemFields({ item: item, itemView: iv }),
              ...getNumberOfOccupants(iv),
            })))
            break
          }
          case 'tourV2':
          case 'addons':
          case 'tourV2Experience': {
            resultItems.push(...item.itemViews.map((iv) => ({
              ...category,
              discountableTotal: sumViewItemTotal(iv),
              luxPlusPrice: isMemberOfHasSubItem ? sumViewItemMemberTotal(iv) : 0,
              numberOfNights: getNumberOfNights(iv),
              itemCountryCode: getItemCountryCode(item),
              reservationType: item.reservationType,
              itemInfoString: buildItemInfoString({ prefix: 'itemType-tourV2-addons-tourv2-experiences-v1', item }),
              offerId: item.offerId,
              itemId: iv.item.itemId,
              transactionKey: iv.item.transactionKey,
              ...discountRequestDatesFromAccommodationCartItem(iv.item),
              ...discountRequestItemLatLngFields(item),
              ...sharedPromoItemFields({ item: item, itemView: iv }),
              ...getNumberOfOccupants(iv),
            })))
            break
          }
          case 'cruise': {
            resultItems.push(...item.itemViews.map((iv) => ({
              ...category,
              discountableTotal: sumViewItemTotal(iv),
              luxPlusPrice: isMemberOfHasSubItem ? sumViewItemMemberTotal(iv) : 0,
              numberOfNights: getNumberOfNights(iv),
              itemCountryCode: getItemCountryCode(item),
              reservationType: item.reservationType,
              itemInfoString: buildItemInfoString({ prefix: 'itemType-cruise-v1.1', item }),
              offerId: item.offerId,
              itemId: iv.item.itemId,
              transactionKey: iv.item.transactionKey,
              ...discountRequestDatesFromAccommodationCartItem(iv.item),
              ...discountRequestItemLatLngFields(item),
              ...sharedPromoItemFields({ item: item, itemView: iv }),
              ...getNumberOfOccupants(iv),
            })))
            break
          }
          default:
            reportClientError(new Error(`promoMap:getPromoItems: Could not map itemType or offerType:${item.itemType} ${item.offerType} ${clientOrderVersion} ${buildItemInfoString({ prefix: 'fallthrough-1', item, fullDetails: false })}`))
        }
      } else if (!item.offerLoaded) {
        // A restored cruise cart is in checkout before its offer loads, so offerType
        // is still unset. Leave the item out until the offer arrives and sets it.
        continue
      } else {
        reportClientError(new Error(`promoMap:getPromoItems: Could not map itemType or offerType ${clientOrderVersion} ${item.offerType} ${buildItemInfoString({ prefix: 'fallthrough-2', item, fullDetails: false })}`))
      }
    }

    if (villaItemsView.data.length > 0) {
      const category = getPromoItemCategoriesFromItemType('villa')
      resultItems.push(...villaItemsView.data.map((iv) => ({
        ...category,
        discountableTotal: sumViewItemTotal(iv),
        luxPlusPrice: isMemberOfHasSubItem ? sum(villaItemsView.data, (i) => i.totals.memberPrice) : 0,
        numberOfNights: getNumberOfNights(iv),
        itemId: iv.item.itemId,
        transactionKey: iv.item.transactionKey,
        offerId: iv.offer.id,
        travellers: getDefaultTravellers(state),
        ...discountRequestDatesFromAccommodationCartItem(iv.item),
        ...sharedPromoItemFields({ itemView: iv }),
        provider: iv.offer.vendorName,
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-villa-items-v1.1', item: iv }),
        ...discountRequestItemLatLngFields(iv),
        ...getNumberOfOccupants(iv),
      })))
    }

    if (giftCardItemsView.data.length > 0) {
      const category = getPromoItemCategoriesFromItemType('gift_card')
      resultItems.push(...giftCardItemsView.data.map((iv) => ({
        ...category,
        discountableTotal: sumViewItemTotal(iv),
        itemId: iv.item.itemId,
        transactionKey: iv.item.transactionKey,
        ...sharedPromoItemFields({ itemView: iv }),
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-gift_card-items-v1.1', item: iv }),
        ...discountRequestItemLatLngFields(iv),
      })))
    }

    const experienceCartFormattedItems = getFormattedExperienceItems(state)

    if (experienceCartFormattedItems.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'experience'

      resultItems.push(...experienceCartFormattedItems.data.map((expCartItem) => {
        const sequence = expCartItem.meta_data?.checkoutExperienceUpsellSequence
        const bundleSequence = expCartItem.meta_data?.checkoutExperienceInflowBundleSequence
        return {
          categoryBK,
          subCategory: getExperienceItemSubCategory(expCartItem.id_experience_items ?? expCartItem.transaction_key),
          discountableTotal: expCartItem.total,
          ...(expCartItem.meta_data?.checkoutExperienceUpsell === true ? {
            checkoutExperienceUpsell: true,
            ...(isExperienceUpsellSequence(sequence) ? { checkoutExperienceUpsellSequence: sequence } : {}),
          } : {}),
          ...(expCartItem.meta_data?.checkoutExperienceInflowBundle === true ? {
            checkoutExperienceInflowBundle: true,
            ...(isExperienceUpsellSequence(bundleSequence) ? { checkoutExperienceInflowBundleSequence: bundleSequence } : {}),
          } : {}),
          luxPlusPrice: 0,
          offerId: expCartItem.provider_offer_id,
          itemId: expCartItem.transaction_key,
          transactionKey: expCartItem.transaction_key,
          ...discountRequestDatesFromExperienceOrderItem(expCartItem),
          ...sharedPromoItemFields({ item: expCartItem, itemView: undefined }),
          provider: expCartItem.title,
          itemInfoString: buildItemInfoString({ prefix: 'itemArray-experience-items-v1.1', item: expCartItem }),
          ...discountRequestItemLatLngFields(expCartItem),
        }
      }))
    }

    if (tourV2ExperienceItemsView.data.length > 0) {
      resultItems.push(...tourV2ExperienceItemsView.data.map((iv) => {
        const category = getPromoItemCategoriesFromItemType(iv.item.itemType)
        return {
          ...category,
          discountableTotal: sumViewItemTotal(iv),
          luxPlusPrice: isMemberOfHasSubItem ? sumViewItemMemberTotal(iv) : 0,
          ...discountRequestDatesFromTourV2ExperienceItem(iv.item),
          ...sharedPromoItemFields({ itemView: iv }),
          offerId: iv.item?.purchasableOption?.fkExperienceId ?? iv.item.itemId,
          provider: iv.item.purchasableOption.location,
          mx: 'mx' in iv.item && typeof iv.item.mx === 'string' ? iv.item.mx : iv.mx ?? undefined,
          loyaltyOfferType: iv.loyalty.productType,
          itemInfoString: buildItemInfoString({ prefix: `itemArray-tourv2-v1.1 iv.item.itemId:${iv.item?.itemId} purchasableOption?.fkExperienceId:${iv.item?.purchasableOption?.fkExperienceId} iv.item.offerId:${iv.item.offerId}`, item: iv }),
          itemId: iv.item?.purchasableOption?.fkExperienceId ?? iv.item.itemId,
          transactionKey: iv.item.transactionKey,
          ...discountRequestItemLatLngFields(iv),
        }
      }))
    }

    if (carHireItemsView.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'car_hire'
      resultItems.push(...carHireItemsView.data.map((vi) => ({
        categoryBK,
        discountableTotal: getCarHireSelectedRateOption(vi.item).payNowAmount,
        luxPlusPrice: 0,
        offerId: vi.item.offerId,
        provider: vi.vendor.name,
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-car-hire-v1.1', item: vi }),
        itemId: vi.item.itemId,
        transactionKey: vi.item.transactionKey,
        ...discountRequestItemDateFields(vi.item.pickUpDate, vi.item.dropOffDate),
        ...discountRequestItemLatLngFields(vi),
        ...sharedPromoItemFields({ itemView: vi }),
        loyaltyOfferType: vi.loyalty.productType,
      })))
    }

    if (subscriptionItemView.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'subscription'
      const joiningSubCategoryBK: DiscountRequestItem['subCategoryBK'] = 'subscription-joining_fee'
      resultItems.push(...subscriptionItemView.data.map((siv) => ({
        categoryBK,
        subCategoryBK: joiningSubCategoryBK,
        discountableTotal: sumViewItemTotal(siv),
        luxPlusPrice: 0,
        offerId: siv.offerId,
        itemId: siv.item.itemId,
        transactionKey: siv.item.transactionKey,
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-subscriptionItemView-v1.1', item: siv }),
        travellers: getDefaultTravellers(state),
        ...discountRequestItemLatLngFields(siv),
        ...sharedPromoItemFields({ itemView: siv }),
      })))
    }

    if (luxPlusSubscriptionItemView.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'subscription'
      const reoccurringFeeSubCategoryBK: DiscountRequestItem['subCategoryBK'] = 'subscription-recurring_fee'
      resultItems.push(...luxPlusSubscriptionItemView.data.map((siv) => ({
        categoryBK,
        subCategoryBK: reoccurringFeeSubCategoryBK,
        discountableTotal: sumViewItemTotal(siv),
        luxPlusPrice: 0,
        offerId: siv.offerId,
        itemId: siv.item.itemId,
        transactionKey: siv.item.transactionKey,
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-luxPlusSubscriptionItemView-v1.1', item: siv }),
        travellers: getDefaultTravellers(state),
        ...discountRequestItemLatLngFields(siv),
        ...sharedPromoItemFields({ itemView: siv }),
      })))
    }

    if (flightItemsView.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'flight'
      const charterSubCategoryBK: DiscountRequestItem['subCategoryBK'] = 'flight-charter'
      const useRegularBundledFlightPrice = !isMemberOfHasSubItem && getIsEnabledFlightsBundledLuxPlusPricing(state)
      const useRegularStandaloneFlightPrice = !isMemberOfHasSubItem && getIsEnabledFlightsLuxPlusPricing(state)
      resultItems.push(...flightItemsView.data.map((view) => {
        const useRegularPrice = view.isBundled ? useRegularBundledFlightPrice : useRegularStandaloneFlightPrice
        const regularPrice = useRegularPrice ? getFlightItemViewLuxPlusRegularPrice(view) : undefined

        return {
          categoryBK,
          ...(view.item.charterBundleRoute ? { subCategoryBK: charterSubCategoryBK } : {}),
          discountableTotal: (regularPrice ?? view.totals.price) + (view.totals.otherFees?.flightSelectedSeatsTotal ?? 0),
          luxPlusPrice: isMemberOfHasSubItem ? view.item.memberTotal : 0,
          offerId: view.item.flights?.[0]?.journeyId,
          itemId: view.item.itemId,
          transactionKey: view.item.transactionKey,
          originAirportCode: view.item.originAirportCode,
          numberOfChildren: view.item.occupants?.children,
          numberOfAdults: view.item.occupants?.adults,
          destinationAirportCode: view.item.destinationAirportCode,
          flightInfo: discountRequestFlightInfo(view),
          itemCountryCode: view.flights ? view.flights.map((fv) => fv.journeyFlight.flights.reduce((prev, f) => `${prev},${f.arrivalCountry}`, '')).join(',') : '',
          provider: view.validatingCarrier ?? view.item.flights?.[0]?.carrier,
          fareType: view.fareType,
          itemInfoString: buildItemInfoString({ prefix: 'itemArray-flights-v1.3', item: view, totals: view.totals }),
          isFnFBooking: view.item.isFnFBooking,
          travellers: getDefaultTravellers(state),
          ...discountRequestDatesFromFlightItem(view.item),
          ...discountRequestItemLatLngFields(view),
          ...sharedPromoItemFields({ itemView: view, isFnFBooking: view.item.isFnFBooking }),
        }
      }))
    }

    if (merchandiseItemsView.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'shop'
      resultItems.push(...merchandiseItemsView.data.map((iv: App.Checkout.MerchandiseItemView) => ({
        categoryBK: categoryBK,
        discountableTotal: iv.totals.price,
        luxPlusPrice: iv.totals.memberPrice,
        offerId: iv.item.advertId,
        itemId: iv.item.itemId,
        transactionKey: iv.item.transactionKey,
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-merchandise-v1.2', item: iv }),
        travellers: getDefaultTravellers(state),
        ...discountRequestItemLatLngFields(iv),
        ...sharedPromoItemFields({ itemView: iv, item: iv.item }),
      })))
    }

    if (merchandiseShippingItemsView.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'shop'
      const subCategoryBK: DiscountRequestItem['subCategoryBK'] = 'shop-shop_shipping'
      resultItems.push(...merchandiseShippingItemsView.data.map((iv: App.Checkout.MerchandiseShippingItemView) => ({
        categoryBK: categoryBK,
        subCategoryBK: subCategoryBK,
        discountableTotal: iv.totals.otherFees?.deliveryFee ?? 0,
        itemId: iv.item.itemId,
        transactionKey: iv.item.transactionKey,
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-merchandise-shipping-v1.1', item: iv }),
        ...discountRequestItemLatLngFields(iv),
        ...sharedPromoItemFields({ itemView: iv }),
      })))
    }

    if (transferItemsView.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'transfer'
      resultItems.push(...transferItemsView.data.map(iv => ({
        categoryBK: categoryBK,
        discountableTotal: iv.totals.price,
        luxPlusPrice: isMemberOfHasSubItem ? iv.totals.memberPrice : 0,
        itemId: iv.item.itemId,
        transactionKey: iv.item.transactionKey,
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-transfer-v1.1', item: iv }),
        travellers: getDefaultTravellers(state),
        ...discountRequestDatesFromTransferItem(iv.item),
        ...discountRequestItemLatLngFields(iv),
        ...sharedPromoItemFields({ itemView: iv }),
      })))
    }

    if (insuranceItemsView.data.length > 0) {
      const categoryBK: DiscountRequestItem['categoryBK'] = 'insurance'
      resultItems.push(...insuranceItemsView.data.map(iv => ({
        categoryBK: categoryBK,
        discountableTotal: iv.totals.price,
        itemId: iv.item.itemId,
        transactionKey: iv.item.transactionKey,
        provider: iv.productId,
        itemInfoString: buildItemInfoString({ prefix: 'itemArray-insurance-v1.1', item: iv }),
        travellers: getDefaultTravellers(state),
        ...discountRequestItemDateFields(iv.startDate, iv.endDate),
        ...discountRequestItemLatLngFields(iv),
        ...sharedPromoItemFields({ itemView: iv ?? undefined }),
      })))
    }
  } catch(err) {
    reportClientError(err)
  }

  return resultItems.map((item) => {
    const offerType = getChannelMarkupOfferType(item)
    if (!offerType) {
      return item
    }

    const itemModifierInfo = getItemModifierInfo(
      channelMarkupSource,
      item,
      offerType,
      useMemberPrice,
      getItemChannelMarkupPricing(state, item, offerType, item.itemId ? rateLeadPricingByItemId.get(item.itemId) : undefined),
    )

    return itemModifierInfo ? { ...item, itemModifierInfo } : item
  })
}

export function getPromoLoyaltyItems(state: App.State): Array<DiscountRequestLoyaltyItem> {
  const cartItems = state.checkout.cart.items
  const transactionKeyByItemId = new Map(cartItems.map((item) => [item.itemId, item.transactionKey]))

  return getCheckoutPointsCalculationRequests(state).map((request) => ({
    itemId: request.itemId ?? '',
    loyaltyProductType: request.loyaltyProductType,
    price: request.price,
    memberPrice: request.memberPrice ?? 0,
    offerId: request.offerId,
    packageId: request.packageId,
    transactionKey: request.itemId ? transactionKeyByItemId.get(request.itemId) : undefined,
  }))
}

/**
 * @deprecated - This is the (very soon) becoming the default method of determining the composition of the cart (i.e. via getAllItemsView)
 * It is just here if we need to fallback to the old priceBreakdownView based method
 */
export const applyPromoWithV2ItemTotals = (state: App.State): boolean => {
  return config.PROMO_CHECKOUT_V2_ITEM_TOTALS_COUNTRY_CODES.includes('ALL') ||
    config.PROMO_CHECKOUT_V2_ITEM_TOTALS_COUNTRY_CODES.includes(state.geo.currentRegionCode) ||
    false
}

/**
 *
 * @param state The current state of the application (in the cart/checkout)
 * @returns The <see cerf="DiscountRequestOrder"/> object that represents the current state of the cart from the perspective of the promo code system.
 * This information will be used to determine if the user qualifies for and calculate the amount of discount each item has (via the App.Promotion and App.Checkout.ItemDiscount Array)
 */
export const stateToDiscountOrder = (state: App.State): DiscountRequestOrder => {
  // Please bump the clientOrderVersion when making changes to the structure of the Discount Request Order
  const clientOrderVersion = PROMO_ORDER_ITEMS_CLIENT_ORDER_VERSION
  return {
    brand: config.BRAND as DiscountRequestOrder['brand'],
    region: state.geo.currentRegionCode as DiscountRequestOrder['region'],
    deviceType: getPromoDeviceType(state),
    hasBedbankPromotion: false,
    isGiftOrder: state.checkout.cart.isGift ?? false,
    items: getPromoItems(state, clientOrderVersion),
    loyaltyItems: getPromoLoyaltyItems(state),
    clientOrderVersion,
  }
}

/**
 * @@deprecated - see filterItemDiscounts/getPromoCodeItemDiscounts (the 'breakdownTitle-based')
 */
export const mapItemTypeToLabel = (type: string) => {
  switch (type) {
    case 'accommodation':
      return 'Accommodation'
    case 'tour':
    case 'tour_v2':
      return 'Tour'
    case 'tour_optional_experience':
      return 'Optional experiences'
    case 'cruise':
      return 'Cruise'
    case 'experience':
      return 'Experience'
    case 'flight':
      return 'Flight'
    case 'insurance':
      return 'Insurance'
    case 'subscription':
      return 'LuxPlus+ Membership'
    case 'booking_protection':
      return 'Cancellation Protection'
      /**
 * @@deprecated - see filterItemDiscounts/getPromoCodeItemDiscounts (the 'breakdownTitle-based')
 */
    case 'car_hire':
      return 'Car Hire'
    case 'gift_card':
      return 'Gift Card'
    default:
      return type
  }
}

/**
 * @@deprecated - see filterItemDiscounts/getPromoCodeItemDiscounts (the 'breakdownTitle-based')
 */
export const mapLabelToItemType = (label: string): BaseSpecificPromoTypeLookup['specificType'] => {
  switch (label) {
    case 'Accommodation':
      return 'accommodation'
    case 'Tour':
      return 'tour'
    case 'Optional experiences':
      return 'tour_optional_experience'
    case 'Cruise':
      return 'cruise'
    case 'Experiences':
    case 'Experience':
      return 'experience'
    case 'Flight':
      return 'flight'
    case 'Insurance':
      return 'insurance'
    case 'LuxPlus+ Membership':
      return 'subscription'
    case 'Cancellation Protection':
    case 'Travel Protection':
      return 'booking_protection'
    case 'Car Hire':
      return 'car_hire'
    case 'Gift Card':
      return 'gift_card'
    case 'Transfers':
      return 'transfer'
    default:
      console.warn(`Could not mapLabelToItemType "${label}"`)
      return 'all'
  }
}
